export const BLOG_ARTICLES = [
  {
    id: 1,
    slug: "preparer-dossier-pret",
    title: "Comment bien préparer son dossier de prêt immobilier ?",
    excerpt: "Les documents essentiels et conseils pour maximiser vos chances d'obtenir votre crédit.",
    category: "Financement",
    emoji: "📋",
    gradient: "#dbeafe, #bfdbfe",
    date: "15 janvier 2025",
    readTime: "5 min",
    content: `
## Les documents indispensables

Pour constituer un dossier de prêt solide, vous devez rassembler plusieurs catégories de documents :

### Pièces d'identité
- Carte d'identité ou passeport en cours de validité
- Justificatif de domicile de moins de 3 mois
- Livret de famille si applicable

### Justificatifs de revenus
- 3 derniers bulletins de salaire
- 2 derniers avis d'imposition
- Contrat de travail (CDI de préférence)

### Situation financière
- 3 derniers relevés de compte bancaire
- Tableau des crédits en cours
- Justificatifs d'épargne

## Conseils pour optimiser votre dossier

### 1. Stabilisez votre situation professionnelle
Les banques privilégient les CDI avec période d'essai terminée. Si vous êtes en CDD ou indépendant, préparez des justificatifs supplémentaires.

### 2. Assainissez vos comptes
Évitez les découverts dans les 3 mois précédant votre demande. Les banques analysent votre comportement bancaire.

### 3. Constituez un apport personnel
Un apport de 10% minimum est généralement demandé. Plus votre apport est important, meilleures seront vos conditions.

### 4. Réduisez votre taux d'endettement
Le taux d'endettement ne doit pas dépasser 35% de vos revenus nets. Pensez à solder certains crédits si nécessaire.

## Le bon moment pour déposer votre dossier

Déposez votre dossier quand vous avez trouvé le bien, mais vous pouvez obtenir un accord de principe avant même de chercher. Cela vous donnera une idée précise de votre budget.
    `
  },
  {
    id: 2,
    slug: "erreurs-investissement-locatif",
    title: "Investissement locatif : les erreurs à éviter",
    excerpt: "Découvrez les pièges courants et comment les éviter pour réussir votre investissement.",
    category: "Investissement",
    emoji: "🏠",
    gradient: "#dcfce7, #bbf7d0",
    date: "10 janvier 2025",
    readTime: "7 min",
    content: `
## Les erreurs les plus fréquentes

### 1. Négliger l'emplacement

L'emplacement est le critère numéro un. Un bien mal situé sera difficile à louer et ne prendra pas de valeur.

À vérifier :- Proximité des transports en commun
- Commerces et services à proximité
- Dynamisme économique de la zone
- Projets d'urbanisme à venir

### 2. Sous-estimer les charges

Beaucoup d'investisseurs calculent leur rentabilité sans prendre en compte toutes les charges :

- Taxe foncière
- Charges de copropriété
- Assurance propriétaire non occupant
- Frais de gestion locative
- Provision pour travaux
- Vacance locative

### 3. Surestimer les loyers

Ne vous fiez pas aux annonces les plus chères. Analysez les loyers réellement pratiqués dans le quartier pour des biens similaires.

### 4. Ignorer la fiscalité

Le régime fiscal choisi impacte fortement votre rentabilité nette :
- Location nue : revenus fonciers
- LMNP : régime micro-BIC ou réel
- Déficit foncier possible dans certains cas

### 5. Ne pas visiter suffisamment

Visitez plusieurs biens avant de vous décider. Ne vous précipitez pas sur la première opportunité.

## Les clés du succès

### Calculez votre rentabilité nette

Utilisez la formule : (Loyers annuels - Charges) / Prix d'achat total × 100

### Diversifiez si possible

Ne mettez pas tous vos œufs dans le même panier. Plusieurs petits biens peuvent être plus sécurisants qu'un seul gros investissement.

### Anticipez la revente

Même si vous investissez sur le long terme, gardez en tête la possibilité de revente. Le bien doit rester attractif.
    `
  },
  {
    id: 3,
    slug: "frais-notaire-guide",
    title: "Frais de notaire : tout ce qu'il faut savoir",
    excerpt: "Comprendre la composition des frais et les moyens de les optimiser.",
    category: "Achat",
    emoji: "💰",
    gradient: "#fef3c7, #fde68a",
    date: "5 janvier 2025",
    readTime: "6 min",
    content: `
## Que comprennent les frais de notaire ?

Les "frais de notaire" regroupent en réalité plusieurs éléments distincts :

### 1. Les droits de mutation (80%)

C'est la part la plus importante, versée au Trésor Public :
- Taxe départementale : entre 3,80% et 4,50% selon les départements
- Taxe communale : 1,20%
- Frais de recouvrement : 2,37% de la taxe départementale

### 2. Les émoluments du notaire (10%)

La rémunération du notaire est réglementée par l'État, calculée selon un barème progressif :
- De 0 à 6 500 € : 3,870%
- De 6 500 à 17 000 € : 1,596%
- De 17 000 à 60 000 € : 1,064%
- Au-delà de 60 000 € : 0,799%

### 3. Les débours et frais annexes (10%)

- Frais de cadastre
- Extraits d'actes
- Timbres fiscaux
- Frais de publication

## Ancien vs Neuf : quelle différence ?

### Dans l'ancien
Les frais représentent environ 7 à 8% du prix d'achat.

### Dans le neuf
Les frais sont réduits à environ 2 à 3% grâce à un taux de taxe de publicité foncière réduit (0,715%).

## Comment réduire ses frais ?

### 1. Négociez les émoluments
Depuis 2016, les notaires peuvent accorder une remise jusqu'à 20% sur leurs émoluments pour les transactions supérieures à 100 000 €.

### 2. Déduisez les meubles
Si le bien est vendu meublé, la valeur des meubles peut être déduite de la base de calcul (dans la limite du raisonnable).

### 3. Séparez les frais d'agence
Si les frais d'agence sont à la charge de l'acheteur ET mentionnés séparément, ils ne rentrent pas dans l'assiette des droits de mutation.

## Quand payer les frais de notaire ?

Les frais sont dus le jour de la signature de l'acte authentique. Le notaire vous demandera généralement de provisionner cette somme quelques jours avant.
    `
  },
  {
    id: 4,
    slug: "premier-achat-immobilier",
    title: "Premier achat immobilier : le guide complet",
    excerpt: "Toutes les étapes pour réussir votre premier achat, de la recherche à la signature.",
    category: "Achat",
    emoji: "🔑",
    gradient: "#e0e7ff, #c7d2fe",
    date: "28 décembre 2024",
    readTime: "10 min",
    content: `
## Étape 1 : Définir votre budget

Avant de commencer vos recherches, déterminez précisément votre capacité d'achat.

### Calculez votre capacité d'emprunt
- Revenus nets mensuels × 35% = mensualité maximale
- Utilisez notre simulateur pour affiner ce calcul

### N'oubliez pas les frais annexes
- Frais de notaire (7-8% dans l'ancien)
- Frais de garantie (1-2%)
- Frais de dossier bancaire
- Déménagement et travaux éventuels

## Étape 2 : Obtenir un accord de principe

Contactez plusieurs banques ou un courtier pour obtenir un accord de principe. Ce document vous permettra de :
- Connaître votre budget exact
- Rassurer les vendeurs
- Négocier en position de force

## Étape 3 : Rechercher le bien

### Définissez vos critères
- Localisation (ville, quartier)
- Surface et nombre de pièces
- Étage et exposition
- Parking, balcon, cave...

### Multipliez les sources
- Sites d'annonces (SeLoger, LeBonCoin, PAP...)
- Agences immobilières locales
- Notaires
- Bouche à oreille

## Étape 4 : Visiter et négocier

### Préparez vos visites
- Liste de questions à poser
- Vérifiez l'état général
- Renseignez-vous sur les charges
- Demandez les diagnostics

### La négociation
Le prix affiché n'est pas forcément le prix final. Une marge de négociation de 5 à 10% est souvent possible.

## Étape 5 : Signer le compromis

Le compromis de vente vous engage. Vous disposez d'un délai de rétractation de 10 jours après signature.

### Les conditions suspensives
- Obtention du prêt (obligatoire)
- Obtention d'un permis de construire
- Vente d'un autre bien...

## Étape 6 : Finaliser le financement

Vous avez généralement 45 à 60 jours pour obtenir votre prêt. Comparez plusieurs offres et négociez :
- Le taux d'intérêt
- L'assurance emprunteur
- Les frais de dossier
- Les indemnités de remboursement anticipé

## Étape 7 : Signer chez le notaire

La signature de l'acte authentique transfère la propriété. Vous recevez les clés et devenez officiellement propriétaire !
    `
  },
  {
    id: 5,
    slug: "negocier-pret-immobilier",
    title: "Comment négocier son prêt immobilier ?",
    excerpt: "Les techniques pour obtenir les meilleures conditions de financement.",
    category: "Financement",
    emoji: "🤝",
    gradient: "#fce7f3, #fbcfe8",
    date: "20 décembre 2024",
    readTime: "6 min",
    content: `
## Ce qui se négocie dans un prêt

### 1. Le taux d'intérêt
C'est l'élément le plus visible, mais pas toujours le plus important. Une différence de 0,1% représente environ 1 000 € sur 20 ans pour un prêt de 200 000 €.

### 2. L'assurance emprunteur
Souvent négligée, l'assurance peut représenter jusqu'à 30% du coût total du crédit. Vous pouvez :
- Négocier le taux avec la banque
- Opter pour une délégation d'assurance (assurance externe)

### 3. Les frais de dossier
Généralement entre 500 € et 1 500 €, ils sont souvent négociables, surtout si votre dossier est solide.

### 4. Les indemnités de remboursement anticipé (IRA)
En cas de revente ou de rachat de crédit, ces indemnités peuvent être coûteuses. Négociez leur suppression ou leur réduction.

## Les leviers de négociation

### Votre profil emprunteur
- CDI avec ancienneté
- Revenus stables et suffisants
- Épargne constituée
- Pas d'incidents bancaires

### La mise en concurrence
Sollicitez plusieurs banques et faites jouer la concurrence. N'hésitez pas à montrer les offres reçues.

### Le moment de l'année
Les banques ont des objectifs commerciaux. Fin de trimestre et fin d'année peuvent être propices à la négociation.

### Les contreparties
- Domiciliation des revenus
- Souscription de produits annexes (assurance habitation, épargne...)
- Transfert de l'épargne existante

## Faire appel à un courtier ?

Un courtier peut vous faire gagner du temps et obtenir de meilleures conditions grâce à son réseau. Ses honoraires (1% du montant emprunté en moyenne) sont souvent compensés par les économies réalisées.
    `
  },
  {
    id: 6,
    slug: "diagnostics-immobiliers",
    title: "Les diagnostics immobiliers obligatoires",
    excerpt: "Tout savoir sur les diagnostics à fournir lors d'une vente ou location.",
    category: "Réglementation",
    emoji: "📊",
    gradient: "#cffafe, #a5f3fc",
    date: "15 décembre 2024",
    readTime: "8 min",
    content: `
## Les diagnostics obligatoires pour la vente

### DPE (Diagnostic de Performance Énergétique)
- Validité : 10 ans
- Obligatoire depuis : 2006
- Classe le bien de A (excellent) à G (passoire thermique)
- Les biens classés F et G sont progressivement interdits à la location

### Diagnostic amiante
- Validité : Illimitée si négatif, 3 ans si positif
- Obligatoire pour : Les biens construits avant le 1er juillet 1997

### Diagnostic plomb (CREP)
- Validité : 1 an si positif, illimitée si négatif
- Obligatoire pour : Les biens construits avant le 1er janvier 1949

### État des risques et pollutions (ERP)
- Validité : 6 mois
- Obligatoire pour : Tous les biens situés dans des zones à risques

### Diagnostic électricité
- Validité : 3 ans
- Obligatoire pour : Installations de plus de 15 ans

### Diagnostic gaz
- Validité : 3 ans
- Obligatoire pour : Installations de plus de 15 ans

### Diagnostic termites
- Validité : 6 mois
- Obligatoire dans : Les zones définies par arrêté préfectoral

### Métrage Loi Carrez
- Validité : Illimitée (sauf travaux)
- Obligatoire pour : Les lots de copropriété

## Qui paie les diagnostics ?

Les diagnostics sont à la charge du vendeur. Comptez entre 200 € et 600 € selon le nombre de diagnostics nécessaires.

## Conséquences en cas d'absence

L'absence de diagnostic peut entraîner :
- L'annulation de la vente
- Une réduction du prix
- La responsabilité du vendeur en cas de vice caché
    `
  },
  {
    id: 7,
    slug: "comprendre-dpe-immobilier",
    title: "DPE : tout comprendre sur le diagnostic de performance énergétique",
    excerpt: "Classes énergétiques, impact sur la valeur du bien, nouvelles réglementations et conseils pour améliorer votre DPE.",
    category: "Réglementation",
    emoji: "⚡",
    gradient: "#fef9c3, #fde047",
    date: "25 janvier 2025",
    readTime: "9 min",
    content: `
## Qu'est-ce que le DPE ?

Le Diagnostic de Performance Énergétique (DPE) est un document obligatoire qui évalue la consommation d'énergie et les émissions de gaz à effet de serre d'un logement.

### Les deux étiquettes

Le DPE attribue deux notes au logement :

Étiquette énergie (consommation)- Classe A : moins de 70 kWh/m²/an (excellent)
- Classe B : 71 à 110 kWh/m²/an
- Classe C : 111 à 180 kWh/m²/an
- Classe D : 181 à 250 kWh/m²/an
- Classe E : 251 à 330 kWh/m²/an
- Classe F : 331 à 420 kWh/m²/an
- Classe G : plus de 420 kWh/m²/an (passoire thermique)

Étiquette climat (émissions CO2)- De A (moins de 6 kg CO2/m²/an) à G (plus de 100 kg CO2/m²/an)

## Le nouveau DPE depuis 2021

Depuis le 1er juillet 2021, le DPE a été entièrement réformé :

### Ce qui a changé

- Méthode de calcul unifiée : La méthode sur factures a été supprimée. Tous les DPE utilisent désormais la méthode 3CL (Calcul de la Consommation Conventionnelle des Logements).

- Opposabilité juridique : Le DPE engage désormais la responsabilité du diagnostiqueur. En cas d'erreur, l'acheteur peut se retourner contre lui.

- Double seuil : La classe finale est déterminée par la plus mauvaise des deux notes (énergie et climat).

- Recommandations de travaux : Le DPE inclut obligatoirement des préconisations pour améliorer la performance du logement.

## Impact sur la location

### Le calendrier des interdictions

Les passoires thermiques sont progressivement interdites à la location :

- Depuis janvier 2023 : Les logements consommant plus de 450 kWh/m²/an sont interdits à la location
- 1er janvier 2025 : Interdiction des logements classés G
- 1er janvier 2028 : Interdiction des logements classés F
- 1er janvier 2034 : Interdiction des logements classés E

### Conséquences pour les propriétaires

Si votre bien est concerné, vous devez :
- Réaliser des travaux de rénovation énergétique
- Ou vendre le bien
- Ou le retirer du marché locatif

## Impact sur la valeur du bien

### La "valeur verte"

Le DPE influence directement le prix de vente :

- Classe A ou B : Plus-value de 5 à 15% par rapport à un bien équivalent en classe D
- Classe F ou G : Décote de 10 à 20% sur le prix de vente

### Négociation à l'achat

Un mauvais DPE est un argument de négociation puissant pour l'acheteur, qui peut demander une réduction correspondant au coût estimé des travaux.

## Comment améliorer son DPE ?

### Les travaux les plus efficaces

1. Isolation thermique- Isolation des combles : gain de 1 à 2 classes possible
- Isolation des murs : gain significatif
- Remplacement des fenêtres : impact modéré mais visible

2. Système de chauffage- Pompe à chaleur : très efficace
- Chaudière à condensation : bon rapport coût/efficacité
- Poêle à granulés : excellent pour le chauffage d'appoint

3. Production d'eau chaude- Chauffe-eau thermodynamique
- Chauffe-eau solaire

### Ordre de priorité recommandé

1. Isolation (toit, murs, sols)
2. Remplacement des fenêtres
3. Système de chauffage
4. Ventilation (VMC double flux)

## Les aides financières disponibles

### MaPrimeRénov'

Aide de l'État calculée selon vos revenus et les travaux réalisés. Peut couvrir jusqu'à 90% du montant des travaux pour les ménages modestes.

### Certificats d'Économies d'Énergie (CEE)

Primes versées par les fournisseurs d'énergie. Cumulables avec MaPrimeRénov'.

### Éco-PTZ

Prêt à taux zéro pour financer les travaux de rénovation énergétique, jusqu'à 50 000 € sur 20 ans.

### TVA réduite

TVA à 5,5% pour les travaux de rénovation énergétique (au lieu de 20%).

## Validité et coût du DPE

### Durée de validité

- DPE réalisés depuis le 1er juillet 2021 : 10 ans
- DPE réalisés entre 2018 et 2021 : Valides jusqu'au 31 décembre 2024
- DPE réalisés avant 2018 : Non valides

### Coût moyen

Le prix d'un DPE varie selon la surface et la localisation :
- Appartement : 100 à 150 €
- Maison : 150 à 250 €

## Conseils pratiques

### Avant de faire réaliser un DPE

- Rassemblez les factures d'énergie
- Préparez les documents sur les travaux réalisés
- Assurez-vous que le diagnostiqueur est certifié

### Pour contester un DPE

Si vous estimez que le DPE est erroné :
1. Demandez des explications au diagnostiqueur
2. Faites réaliser un contre-diagnostic
3. En cas de préjudice, engagez une procédure

### Pour optimiser votre DPE sans travaux

- Faites réaliser le diagnostic en période de chauffe
- Signalez tous les équipements performants
- Mentionnez les travaux récents avec justificatifs
    `
  },
  {
    id: 8,
    slug: "ptz-2025-guide-complet",
    title: "PTZ 2025 : le guide complet du prêt à taux zéro",
    excerpt: "Conditions d'éligibilité, zones géographiques, montants et simulation du PTZ pour votre premier achat.",
    category: "Financement",
    emoji: "🏦",
    gradient: "#dbeafe, #93c5fd",
    date: "28 janvier 2025",
    readTime: "8 min",
    content: `
## Qu'est-ce que le PTZ ?

Le Prêt à Taux Zéro (PTZ) est un prêt aidé par l'État, sans intérêts ni frais de dossier, destiné à aider les primo-accédants à financer leur résidence principale.

### Les avantages du PTZ

- Aucun intérêt : Vous ne remboursez que le capital emprunté
- Différé de remboursement : Possibilité de ne rien rembourser pendant 5 à 15 ans
- Complément de financement : Se combine avec un prêt principal
- Pas de frais de dossier : Aucun coût supplémentaire

## Conditions d'éligibilité 2025

### Être primo-accédant

Vous ne devez pas avoir été propriétaire de votre résidence principale au cours des 2 dernières années.

Exceptions :- Titulaires d'une carte d'invalidité
- Bénéficiaires de l'AAH ou de l'AEEH
- Victimes d'une catastrophe naturelle ou technologique

### Plafonds de ressources

Vos revenus ne doivent pas dépasser certains plafonds selon la zone et la composition du foyer :

Zone A bis et A (Paris et grandes villes) :- 1 personne : 49 000 €
- 2 personnes : 73 500 €
- 3 personnes : 88 200 €
- 4 personnes : 102 900 €
- 5 personnes et + : 117 600 €

Zone B1 (villes moyennes) :- 1 personne : 34 500 €
- 2 personnes : 51 750 €
- 3 personnes : 62 100 €
- 4 personnes : 72 450 €
- 5 personnes et + : 82 800 €

Zone B2 et C (zones rurales) :- 1 personne : 31 500 €
- 2 personnes : 47 250 €
- 3 personnes : 56 700 €
- 4 personnes : 66 150 €
- 5 personnes et + : 75 600 €

## Types de biens éligibles

### Dans le neuf (toutes zones)

- Logements neufs (VEFA ou construction)
- Logements anciens avec travaux importants (25% du coût total)

### Dans l'ancien (zones B2 et C uniquement)

- Logements nécessitant des travaux représentant au moins 25% du coût total
- Travaux d'amélioration ou de modernisation

## Montant du PTZ

### Quotité finançable

Le PTZ peut financer une partie du coût de l'opération :
- Zone A bis et A : 40% du coût (neuf uniquement)
- Zone B1 : 40% du coût (neuf uniquement)
- Zone B2 et C : 20% du coût (neuf) ou 40% (ancien avec travaux)

### Plafonds d'opération

Le montant de l'opération pris en compte est plafonné :

Zone A bis :- 1 personne : 150 000 €
- 2 personnes : 225 000 €
- 3 personnes : 270 000 €
- 4 personnes : 315 000 €
- 5 personnes et + : 360 000 €

### Exemple de calcul

Pour un couple avec 1 enfant en zone B1, achetant un logement neuf à 280 000 € :
- Plafond d'opération : 255 000 €
- Quotité : 40%
- PTZ maximum : 255 000 € × 40% = 102 000 €
## Durée et remboursement

### Durée totale

La durée du PTZ varie de 20 à 25 ans selon vos revenus.

### Période de différé

Pendant cette période, vous ne remboursez que votre prêt principal :
- Tranche 1 (revenus les plus modestes) : 15 ans de différé
- Tranche 2 : 10 ans de différé
- Tranche 3 : 5 ans de différé

### Période de remboursement

Après le différé, vous remboursez le PTZ sur 10 à 15 ans.

## Comment obtenir un PTZ ?

### Étape 1 : Vérifiez votre éligibilité

Utilisez un simulateur en ligne ou consultez votre banque pour vérifier que vous remplissez les conditions.

### Étape 2 : Constituez votre dossier

Documents nécessaires :
- Avis d'imposition N-2
- Justificatifs d'identité
- Compromis de vente ou contrat de réservation
- Plan de financement

### Étape 3 : Déposez votre demande

Le PTZ est distribué par les banques ayant signé une convention avec l'État. Comparez les offres car le prêt principal associé peut varier.

## Cumul avec d'autres aides

Le PTZ est cumulable avec :
- Prêt Action Logement (ex 1% logement)
- Prêt d'accession sociale (PAS)
- Prêt conventionné
- Prêt épargne logement (PEL/CEL)
- Aides locales des collectivités

## Nouveautés 2025

### Élargissement des zones

Le PTZ est désormais accessible dans toutes les zones pour le neuf, avec des conditions adaptées.

### Revalorisation des plafonds

Les plafonds de ressources ont été revalorisés pour tenir compte de l'inflation.

### Simplification des démarches

Les procédures de demande ont été simplifiées avec une dématérialisation accrue.
    `
  },
  {
    id: 9,
    slug: "lmnp-guide-complet",
    title: "LMNP : le guide complet du loueur meublé non professionnel",
    excerpt: "Statut fiscal avantageux, amortissement, régimes d'imposition et déclarations pour optimiser votre investissement locatif meublé.",
    category: "Investissement",
    emoji: "🛋️",
    gradient: "#fae8ff, #e879f9",
    date: "22 janvier 2025",
    readTime: "10 min",
    content: `
## Qu'est-ce que le statut LMNP ?

Le Loueur en Meublé Non Professionnel (LMNP) est un statut fiscal permettant de louer un bien meublé tout en bénéficiant d'avantages fiscaux significatifs.

### Conditions pour être LMNP

Pour bénéficier du statut LMNP, vous devez :
- Louer un logement meublé (équipements obligatoires listés par décret)
- Percevoir moins de 23 000 € de recettes locatives annuelles OU que ces recettes représentent moins de 50% de vos revenus globaux

Au-delà de ces seuils, vous passez en LMP (Loueur Meublé Professionnel).

## Les équipements obligatoires

Pour qu'un logement soit considéré comme meublé, il doit contenir au minimum :

- Literie avec couette ou couverture
- Volets ou rideaux dans les chambres
- Plaques de cuisson
- Four ou four micro-ondes
- Réfrigérateur avec compartiment congélateur
- Vaisselle et ustensiles de cuisine
- Table et sièges
- Étagères de rangement
- Luminaires
- Matériel d'entretien ménager

## Les deux régimes fiscaux

### Régime Micro-BIC

Conditions : Recettes annuelles inférieures à 77 700 €

Fonctionnement :- Abattement forfaitaire de 50% sur les recettes
- Vous êtes imposé sur les 50% restants
- Aucune comptabilité complexe à tenir

Exemple : Pour 12 000 € de loyers annuels :
- Base imposable : 12 000 € × 50% = 6 000 €
- Imposition selon votre tranche marginale

### Régime Réel

Fonctionnement :- Déduction de toutes les charges réelles
- Possibilité d'amortir le bien et les meubles
- Comptabilité obligatoire (expert-comptable recommandé)

Charges déductibles :- Intérêts d'emprunt
- Assurances
- Taxe foncière
- Charges de copropriété
- Travaux d'entretien
- Frais de gestion
- Honoraires comptables

## L'amortissement : l'atout majeur du réel

### Principe de l'amortissement

L'amortissement permet de déduire chaque année une partie de la valeur du bien, sans sortie de trésorerie.

### Durées d'amortissement

- Construction : 25 à 40 ans (2,5% à 4% par an)
- Terrain : Non amortissable (environ 20% de la valeur)
- Meubles : 5 à 10 ans
- Travaux : 10 à 15 ans selon leur nature

### Exemple concret

Achat d'un appartement à 200 000 € :
- Valeur du terrain (20%) : 40 000 € → Non amortissable
- Valeur construction (80%) : 160 000 € → Amortissement sur 30 ans
- Amortissement annuel : 160 000 € / 30 = 5 333 €/an
Ajoutez l'amortissement des meubles (10 000 € sur 7 ans = 1 428 €/an), et vous obtenez près de 7 000 € de charges déductibles sans décaissement !

## Comparaison Micro-BIC vs Réel

### Micro-BIC
- Simplicité : Très simple
- Optimisation fiscale : Limitée
- Déficit reportable : Non
- Adapté si : Charges inférieures à 50% des recettes

### Régime Réel
- Simplicité : Comptabilité obligatoire
- Optimisation fiscale : Maximale avec amortissement
- Déficit reportable : Oui, sur 10 ans
- Adapté si : Charges supérieures à 50% des recettes

Conseil : Le régime réel est presque toujours plus avantageux, surtout les premières années avec les intérêts d'emprunt élevés.

## Déclarations et formalités

### À l'acquisition

1. Déclaration P0i : À envoyer au greffe du tribunal de commerce dans les 15 jours suivant le début d'activité
2. Option pour le régime réel : Si vous le souhaitez, dans les délais de la première déclaration

### Chaque année

- Liasse fiscale : Formulaires 2031 et annexes (régime réel)
- Déclaration de revenus : Report sur le formulaire 2042 C PRO

## Avantages et inconvénients

### Avantages

- ✅ Fiscalité avantageuse grâce à l'amortissement
- ✅ Loyers généralement plus élevés qu'en location nue
- ✅ Flexibilité des baux (1 an, 9 mois pour étudiants)
- ✅ Récupération de TVA possible sur le neuf
- ✅ Déficit reportable sur 10 ans

### Inconvénients

- ❌ Gestion plus lourde (turnover, équipements)
- ❌ Comptabilité obligatoire en régime réel
- ❌ Coût d'ameublement initial
- ❌ Plus-value calculée sans tenir compte des amortissements

## Cas particuliers

### Location saisonnière (Airbnb)

Le LMNP s'applique également aux locations saisonnières. Attention cependant :
- Réglementation locale à respecter
- Déclaration en mairie obligatoire
- Limitation à 120 jours/an pour la résidence principale

### Résidences services

Investir en LMNP dans une résidence services (étudiants, seniors, tourisme) permet de :
- Bénéficier de la gestion par un exploitant
- Récupérer la TVA sur l'achat (sous conditions)
- Percevoir des loyers garantis par bail commercial
    `
  },
  {
    id: 10,
    slug: "assurance-emprunteur-economiser",
    title: "Assurance emprunteur : comment économiser jusqu'à 50% ?",
    excerpt: "Délégation d'assurance, comparaison, résiliation et conseils pour réduire le coût de votre assurance de prêt immobilier.",
    category: "Financement",
    emoji: "🛡️",
    gradient: "#ccfbf1, #5eead4",
    date: "18 janvier 2025",
    readTime: "7 min",
    content: `
## L'assurance emprunteur : un coût majeur

L'assurance emprunteur peut représenter jusqu'à 30% du coût total de votre crédit immobilier. Sur un prêt de 200 000 € sur 20 ans, la différence entre une assurance bancaire et une assurance déléguée peut atteindre 15 000 à 20 000 €.

### Ce que couvre l'assurance

Les garanties principales :
- Décès : Le capital restant dû est remboursé
- PTIA (Perte Totale et Irréversible d'Autonomie)
- ITT (Incapacité Temporaire de Travail)
- IPT (Invalidité Permanente Totale)
- IPP (Invalidité Permanente Partielle)

## La délégation d'assurance

### Qu'est-ce que c'est ?

Depuis la loi Lagarde (2010), vous n'êtes pas obligé de souscrire l'assurance proposée par votre banque. Vous pouvez choisir un assureur externe offrant des garanties équivalentes.

### Pourquoi c'est avantageux ?

- Tarifs plus compétitifs : Les assureurs externes proposent souvent des tarifs 30 à 50% moins chers
- Tarification personnalisée : Prix basé sur votre profil réel (âge, santé, profession)
- Garanties sur mesure : Adaptées à votre situation

### Comment procéder ?

1. Demandez la fiche standardisée d'information (FSI) à votre banque
2. Comparez les offres d'assureurs externes
3. Vérifiez l'équivalence des garanties
4. Soumettez le contrat à votre banque
5. La banque a 10 jours pour accepter ou refuser (motif écrit obligatoire)

## Changer d'assurance en cours de prêt

### La loi Lemoine (2022)

Depuis le 1er septembre 2022, vous pouvez résilier votre assurance emprunteur à tout moment, sans frais, et sans attendre la date anniversaire.

### Procédure de résiliation

1. Trouvez une nouvelle assurance avec garanties équivalentes
2. Envoyez votre demande à la banque avec le nouveau contrat
3. Attendez la réponse : 10 jours ouvrés maximum
4. La banque édite un avenant au prêt avec la nouvelle assurance

### Délais à respecter

- Résiliation possible à tout moment
- La nouvelle assurance doit couvrir le capital restant dû
- Pas de frais de résiliation ni pénalités

## Comparer les offres efficacement

### Les critères essentiels

Le TAEA (Taux Annuel Effectif d'Assurance)Permet de comparer le coût réel des assurances entre elles.

La quotitéPourcentage du capital couvert. Pour un emprunteur seul : 100%. Pour un couple : répartition au choix (ex : 50/50 ou 100/100).

Les exclusionsAttention aux sports à risque, professions dangereuses, pathologies préexistantes.

Les délais de carence et de franchise- Carence : période pendant laquelle vous n'êtes pas couvert
- Franchise : durée avant indemnisation en cas de sinistre

### Tableau comparatif type

### Assurance banque
- Coût mensuel : 80 €
- Coût total sur 20 ans : 19 200 €
- TAEA : 0,45%

### Assurance externe
- Coût mensuel : 45 €
- Coût total sur 20 ans : 10 800 €
- TAEA : 0,25%
- Économie réalisée : 8 400 €

## Questionnaire de santé

### Ce qui a changé

Depuis la loi Lemoine, le questionnaire de santé est supprimé pour :
- Les prêts inférieurs à 200 000 € par assuré
- Les prêts remboursés avant les 60 ans de l'emprunteur

### Droit à l'oubli

Vous n'avez plus à déclarer :
- Un cancer guéri depuis plus de 5 ans
- Une hépatite C guérie depuis plus de 5 ans

### Convention AERAS

Pour les personnes ayant ou ayant eu des problèmes de santé graves, la convention AERAS facilite l'accès à l'assurance et au crédit.

## Conseils pour économiser

### 1. Comparez avant de signer le prêt

Négociez la délégation d'assurance dès la demande de prêt. C'est le moment où vous avez le plus de pouvoir de négociation.

### 2. Utilisez les comparateurs en ligne

Des sites comme Magnolia, Réassurez-moi ou Empruntis permettent de comparer rapidement les offres.

### 3. Adaptez la quotité

Pour un couple, une quotité 100/100 double le coût. Analysez si 50/50 ou 70/30 ne serait pas suffisant selon vos situations professionnelles.

### 4. Renégociez régulièrement

Les tarifs évoluent, et votre situation aussi. N'hésitez pas à comparer chaque année, surtout si vous avez arrêté de fumer ou amélioré votre santé.

### 5. Attention aux fausses économies

Ne sacrifiez pas les garanties essentielles pour économiser quelques euros. Une couverture insuffisante peut coûter très cher en cas de sinistre.
    `
  },
  {
    id: 11,
    slug: "taxe-fonciere-guide",
    title: "Taxe foncière : comment est-elle calculée et comment la réduire ?",
    excerpt: "Comprendre le calcul de la taxe foncière, les exonérations possibles et les recours en cas de montant contestable.",
    category: "Fiscalité",
    emoji: "🏛️",
    gradient: "#fee2e2, #fca5a5",
    date: "12 janvier 2025",
    readTime: "6 min",
    content: `
## Qu'est-ce que la taxe foncière ?

La taxe foncière sur les propriétés bâties (TFPB) est un impôt local dû par tout propriétaire d'un bien immobilier au 1er janvier de l'année d'imposition.

### Qui doit la payer ?

- Les propriétaires (y compris en indivision)
- Les usufruitiers
- Les titulaires d'un bail emphytéotique ou à construction

Important : Même si vous vendez en cours d'année, vous devez la taxe entière. Une répartition prorata temporis se fait généralement entre vendeur et acheteur lors de la vente.

## Comment est-elle calculée ?

### La formule de base

Taxe foncière = Valeur locative cadastrale × Taux d'imposition
### La valeur locative cadastrale

C'est le loyer annuel théorique que pourrait produire le bien. Elle est déterminée par l'administration fiscale selon :

- La surface du bien
- La catégorie du logement (confort, standing)
- La situation géographique
- L'état d'entretien

Cette valeur est ensuite diminuée d'un abattement forfaitaire de 50% pour tenir compte des frais de gestion et d'entretien.

### Les taux d'imposition

Ils sont votés chaque année par les collectivités locales :
- Commune
- Intercommunalité (EPCI)
- Éventuellement : taxes spéciales (GEMAPI, etc.)

### Exemple de calcul

Valeur locative cadastrale : 5 000 €
- Base d'imposition : 5 000 € × 50% = 2 500 €
- Taux communal : 25%
- Taux intercommunal : 5%
- Taxe foncière = 2 500 € × 30% = 750 €
## Pourquoi la taxe foncière augmente ?

### Revalorisation annuelle

Chaque année, les valeurs locatives sont revalorisées par un coefficient voté en loi de finances. En 2024, cette revalorisation était de 3,9%.

### Augmentation des taux locaux

Les collectivités peuvent voter des hausses de taux pour financer leurs dépenses.

### Révision des valeurs locatives

Une réforme progressive des valeurs locatives est en cours, basée sur les loyers réels du marché.

### Suppression de la taxe d'habitation

La suppression de la taxe d'habitation pour les résidences principales a parfois conduit les communes à augmenter la taxe foncière pour compenser.

## Les exonérations possibles

### Exonérations temporaires (construction neuve)

- 2 ans d'exonération pour les constructions nouvelles
- Déclaration obligatoire dans les 90 jours suivant l'achèvement (formulaire H1 ou H2)

### Exonérations permanentes

Personnes âgées ou handicapées :- Plus de 75 ans au 1er janvier
- Bénéficiaires de l'ASPA ou de l'ASI
- Titulaires de l'AAH
- Sous conditions de revenus

Biens spécifiques :- Bâtiments ruraux affectés à un usage agricole
- Bâtiments affectés à la méthanisation agricole

### Exonérations pour travaux énergétiques

Certaines collectivités accordent une exonération partielle (50 à 100%) pendant 3 ans pour les logements ayant fait l'objet de travaux d'économie d'énergie.

## Comment contester sa taxe foncière ?

### Vérifiez votre avis d'imposition

Contrôlez :
- La surface indiquée
- Le nombre de pièces
- Les éléments de confort
- La catégorie du bien

### Demandez une révision

Si vous constatez des erreurs, vous pouvez demander une révision de la valeur locative cadastrale :

1. Réclamation auprès du centre des impôts avant le 31 décembre de l'année suivant la mise en recouvrement
2. Joignez les justificatifs (plan du logement, photos, etc.)
3. Attendez la réponse : L'administration a 6 mois pour répondre

### Cas de réduction possible

- Surface habitable surestimée
- Éléments de confort inexistants ou supprimés
- Travaux ayant diminué la valeur du bien
- Erreur de catégorie du logement

## Conseils pratiques

### Anticipez le paiement

La taxe foncière est due mi-octobre. Optez pour le prélèvement mensuel pour étaler la charge (10 mensualités de janvier à octobre).

### Déclarez vos travaux

Si vous faites des travaux diminuant la surface ou le confort, déclarez-les pour faire réviser la valeur locative.

### Conservez vos justificatifs

Gardez les documents relatifs à votre bien : plans, permis de construire, factures de travaux. Ils peuvent être utiles en cas de contestation.

### Vérifiez les exonérations locales

Renseignez-vous auprès de votre mairie sur les éventuelles exonérations pour travaux énergétiques ou autres dispositifs locaux.
    `
  },
  {
    id: 12,
    slug: "sci-investissement-immobilier",
    title: "SCI : faut-il investir en société civile immobilière ?",
    excerpt: "Avantages, inconvénients, fiscalité et démarches pour créer une SCI et optimiser votre patrimoine immobilier.",
    category: "Investissement",
    emoji: "🏢",
    gradient: "#e0e7ff, #c7d2fe",
    date: "8 janvier 2025",
    readTime: "9 min",
    content: `
## Qu'est-ce qu'une SCI ?

La Société Civile Immobilière (SCI) est une structure juridique permettant à plusieurs personnes de détenir et gérer ensemble un ou plusieurs biens immobiliers.

### Caractéristiques principales

- Minimum 2 associés (personnes physiques ou morales)
- Capital social libre (à partir de 1 €)
- Objet exclusivement civil (pas d'activité commerciale)
- Gestion par un ou plusieurs gérants

### Les différents types de SCI

- SCI familiale : Entre membres d'une même famille
- SCI de gestion/location : Pour détenir et louer des biens
- SCI de construction-vente : Pour construire et revendre
- SCI d'attribution : Pour attribuer des lots à chaque associé

## Avantages de la SCI

### 1. Faciliter la transmission

- Donation progressive de parts sociales
- Évite l'indivision successorale
- Abattements fiscaux renouvelables tous les 15 ans
- Possibilité de démembrement (usufruit/nue-propriété)

### 2. Protéger le patrimoine

- Séparation patrimoine personnel/professionnel
- Protection du conjoint (clause d'agrément)
- Maintien du bien dans la famille

### 3. Optimisation fiscale

- Choix entre IR (impôt sur le revenu) et IS (impôt sur les sociétés)
- Déduction des charges et travaux
- Possibilité d'amortissement en IS

### 4. Gestion facilitée

- Prise de décision à la majorité définie dans les statuts
- Évite les blocages de l'indivision
- Flexibilité dans la répartition des parts

## Inconvénients de la SCI

### 1. Formalités administratives

- Rédaction des statuts
- Immatriculation au RCS
- Tenue d'une comptabilité
- Assemblées générales annuelles
- Publication des comptes (IS)

### 2. Coûts de création et de gestion

- Frais de création : 200 à 1 500 €
- Comptabilité annuelle : 500 à 2 000 €
- Formalités diverses

### 3. Responsabilité des associés

- Responsabilité indéfinie et non solidaire
- Les associés sont responsables des dettes sur leur patrimoine personnel

### 4. Impossibilité de louer en meublé

- La SCI à l'IR ne peut pas faire de location meublée (activité commerciale)
- Solution : opter pour l'IS ou créer une SARL de famille

## Fiscalité de la SCI

### SCI à l'IR (transparence fiscale)

- Les bénéfices sont imposés directement chez les associés
- Revenus fonciers classiques
- Déficit foncier imputable (dans la limite de 10 700 €/an)
- Plus-values des particuliers (exonération après 22 ans)

### SCI à l'IS (opacité fiscale)

- Imposition au niveau de la société
- Taux IS : 15% jusqu'à 42 500 € puis 25%
- Amortissement du bien possible
- Déficits reportables sans limite
- Plus-values professionnelles (pas d'exonération)

### Comparaison IR vs IS

En IR :
- Simplicité de gestion
- Plus-values exonérées à terme
- Pas d'amortissement

En IS :
- Amortissement déductible
- Taux d'imposition potentiellement plus faible
- Réinvestissement des bénéfices facilité
- Imposition à la sortie (dividendes + plus-value)

## Créer une SCI : les étapes

### Étape 1 : Rédiger les statuts

Les statuts doivent contenir :
- Dénomination sociale
- Siège social
- Objet social
- Capital social et répartition des parts
- Durée de la société (99 ans maximum)
- Modalités de gestion

### Étape 2 : Apports au capital

- Apports en numéraire (argent)
- Apports en nature (bien immobilier existant)
- Attention aux droits d'enregistrement en cas d'apport immobilier

### Étape 3 : Immatriculation

1. Publier une annonce légale
2. Déposer le dossier au greffe du tribunal de commerce
3. Obtenir le Kbis

### Étape 4 : Gestion courante

- Tenir une comptabilité
- Convoquer les AG annuelles
- Déposer les comptes (si IS)

## SCI familiale : cas particulier

### Avantages spécifiques

- Transmission facilitée aux enfants
- Possibilité de garder le contrôle (gérance)
- Protection du conjoint survivant

### Stratégie de transmission

1. Créer la SCI avec les parents gérants
2. Donner progressivement les parts aux enfants
3. Conserver l'usufruit pour continuer à percevoir les revenus
4. Au décès : les enfants récupèrent la pleine propriété sans droits

### Exemple chiffré

Bien immobilier de 400 000 € :
- Donation directe : droits élevés
- Via SCI avec démembrement :
- Valeur de la nue-propriété (enfants de 50 ans) : environ 50%
- Donation de 200 000 € de parts
- Abattement de 100 000 € par parent et par enfant
- Droits de donation fortement réduits

## Quand choisir la SCI ?

### Situations favorables

- Achat à plusieurs (couple non marié, amis, famille)
- Objectif de transmission patrimoniale
- Détention de plusieurs biens
- Volonté de structurer la gestion

### Situations à éviter

- Achat d'une résidence principale seul
- Location meublée souhaitée (préférer LMNP ou SARL)
- Projet simple sans enjeu de transmission
- Aversion pour la paperasse administrative
    `
  },
  {
    id: 13,
    slug: "achat-nom-propre-avantages",
    title: "Acheter en nom propre : avantages et limites",
    excerpt: "Pourquoi l'achat en nom propre reste souvent le meilleur choix pour les investisseurs et propriétaires occupants.",
    category: "Achat",
    emoji: "👤",
    gradient: "#fef3c7, #fde68a",
    date: "5 janvier 2025",
    readTime: "7 min",
    content: `
## Qu'est-ce que l'achat en nom propre ?

L'achat en nom propre signifie acquérir un bien immobilier directement, en tant que personne physique, sans passer par une structure juridique (SCI, SARL, etc.).

### Principe

- Vous êtes propriétaire du bien à titre personnel
- Le bien fait partie de votre patrimoine privé
- Vous êtes directement responsable des obligations liées au bien

## Avantages de l'achat en nom propre

### 1. Simplicité maximale

- Aucune structure à créer
- Pas de statuts à rédiger
- Pas de comptabilité de société
- Pas d'assemblées générales

### 2. Coûts réduits

- Pas de frais de création de société
- Pas d'honoraires comptables obligatoires
- Moins de formalités administratives

### 3. Fiscalité avantageuse pour la résidence principale

- Exonération totale de plus-value à la revente
- Pas d'impôt sur les loyers fictifs
- Abattement sur la taxe foncière (selon communes)

### 4. Accès facilité au crédit

- Les banques préfèrent prêter aux particuliers
- Taux souvent plus avantageux
- Garanties plus simples (hypothèque, caution)

### 5. Régimes fiscaux attractifs

- LMNP (Loueur Meublé Non Professionnel)
- Dispositifs Pinel, Denormandie
- Déficit foncier
- Loi Malraux, Monuments Historiques

## Fiscalité en nom propre

### Location nue (revenus fonciers)

Régime micro-foncier (si revenus < 15 000 €/an) :
- Abattement forfaitaire de 30%
- Déclaration simplifiée

Régime réel :
- Déduction des charges réelles
- Possibilité de déficit foncier (limité à 10 700 €/an)
- Intéressant si travaux importants

### Location meublée (LMNP)

Régime micro-BIC :
- Abattement de 50%
- Simple mais moins optimisé

Régime réel :
- Amortissement du bien et des meubles
- Déduction de toutes les charges
- Souvent plus avantageux

### Plus-values immobilières

- Abattement progressif pour durée de détention
- Exonération totale après 22 ans (IR) et 30 ans (prélèvements sociaux)
- Exonération immédiate pour la résidence principale

## Limites de l'achat en nom propre

### 1. Pas de protection du patrimoine

- Le bien fait partie de votre patrimoine personnel
- Saisissable par les créanciers personnels
- Pas de cloisonnement des risques

### 2. Transmission complexe

- Succession en indivision
- Risques de conflits entre héritiers
- Droits de succession potentiellement élevés

### 3. Pas de choix de l'IS

- Impossible d'opter pour l'impôt sur les sociétés
- Imposition obligatoire au barème progressif (revenus fonciers)

### 4. Gestion à plusieurs difficile

- Indivision source de blocages
- Unanimité requise pour les décisions importantes
- Un indivisaire peut demander le partage

## Achat en nom propre vs SCI

### Critères de choix

Pour la résidence principale :
- Nom propre recommandé (exonération plus-value)

Pour l'investissement locatif seul :
- Nom propre souvent suffisant
- LMNP très avantageux

Pour acheter à plusieurs (hors couple marié) :
- SCI préférable pour éviter l'indivision

Pour transmettre à ses enfants :
- SCI plus efficace fiscalement

Pour plusieurs biens :
- SCI peut faciliter la gestion

### Comparaison résumée

En nom propre :
- Simplicité : Maximale
- Coût de gestion : Faible
- Crédit : Facile
- Transmission : Complexe
- Protection : Aucune

En SCI :
- Simplicité : Moyenne
- Coût de gestion : Élevé
- Crédit : Plus difficile
- Transmission : Facilitée
- Protection : Partielle

## Cas pratiques

### Cas 1 : Achat de résidence principale

Situation : Couple marié, premier achat
Recommandation : Nom propre
Raison : Simplicité, exonération plus-value, crédit facilité

### Cas 2 : Premier investissement locatif

Situation : Salarié, achat d'un studio pour louer
Recommandation : Nom propre en LMNP
Raison : Fiscalité optimisée, pas de structure à gérer

### Cas 3 : Achat à plusieurs non mariés

Situation : Frères et sœurs achètent ensemble
Recommandation : SCI
Raison : Évite l'indivision, facilite la gestion

### Cas 4 : Constitution de patrimoine pour transmission

Situation : Parents souhaitant transmettre à leurs enfants
Recommandation : SCI familiale
Raison : Donation progressive de parts, démembrement possible

## Conseils pratiques

### Avant d'acheter

- Définissez votre objectif (habitation, location, transmission)
- Évaluez si vous serez seul ou à plusieurs
- Anticipez la durée de détention
- Consultez un notaire ou expert-comptable si besoin

### Pendant la détention

- Conservez tous les justificatifs de travaux
- Déclarez correctement vos revenus
- Anticipez les échéances fiscales

### À la revente

- Calculez votre plus-value imposable
- Vérifiez les conditions d'exonération
- Pensez au réinvestissement
    `
  },
  {
    id: 14,
    slug: "maprimenov-2026-guide-complet",
    title: "MaPrimeRénov' 2026 : Le Guide Complet des Aides à la Rénovation",
    excerpt: "Tout savoir sur MaPrimeRénov' en 2026 : montants, conditions, travaux éligibles et démarches pour obtenir cette aide à la rénovation énergétique.",
    category: "Réglementation",
    emoji: "🏠",
    gradient: "#d1fae5, #a7f3d0",
    date: "31 janvier 2026",
    readTime: "8 min",
    content: `
## Qu'est-ce que MaPrimeRénov' ?

MaPrimeRénov' est la principale aide de l'État français pour la rénovation énergétique des logements. Elle remplace l'ancien crédit d'impôt transition énergétique (CITE) et les aides de l'Anah "Habiter Mieux Agilité".

En 2026, le dispositif continue d'évoluer pour encourager les rénovations globales et performantes.

## Les nouveautés 2026

### Priorité aux rénovations d'ampleur

- Les rénovations mono-geste sont désormais moins subventionnées
- Focus sur les rénovations permettant un gain de 2 classes énergétiques minimum
- Accompagnement obligatoire par Mon Accompagnateur Rénov' pour les projets importants

### Évolution des plafonds

Les montants maximaux ont été revus pour s'adapter à l'inflation et aux coûts réels des travaux.

## Les conditions d'éligibilité

### Qui peut en bénéficier ?

- Propriétaires occupants (résidence principale)
- Propriétaires bailleurs (engagement de location 6 ans minimum)
- Copropriétés (pour les parties communes)
- Usufruitiers

### Conditions sur le logement

- Logement construit depuis plus de 15 ans
- Situé en France métropolitaine ou Outre-mer
- Utilisé comme résidence principale (occupation 8 mois/an minimum)

## Les catégories de revenus

MaPrimeRénov' fonctionne avec 4 profils de revenus, déterminés selon le revenu fiscal de référence et la composition du foyer.

### MaPrimeRénov' Bleu (revenus très modestes)

Plafonds de revenus en Île-de-France :
- 1 personne : 23 541 euros
- 2 personnes : 34 551 euros
- 3 personnes : 41 493 euros
- 4 personnes : 48 447 euros
- Par personne supplémentaire : +6 059 euros

### MaPrimeRénov' Jaune (revenus modestes)

Plafonds de revenus en Île-de-France :
- 1 personne : 28 657 euros
- 2 personnes : 42 058 euros
- 3 personnes : 50 513 euros
- 4 personnes : 58 981 euros
- Par personne supplémentaire : +7 422 euros

### MaPrimeRénov' Violet (revenus intermédiaires)

Plafonds de revenus en Île-de-France :
- 1 personne : 40 018 euros
- 2 personnes : 58 827 euros
- 3 personnes : 70 382 euros
- 4 personnes : 82 839 euros
- Par personne supplémentaire : +10 359 euros

### MaPrimeRénov' Rose (revenus supérieurs)

Au-delà des plafonds violet, avec des aides réduites mais toujours disponibles pour certains travaux.

## Les travaux éligibles et montants

### Chauffage et eau chaude

Pompe à chaleur air/eau :
- Bleu : 5 000 euros
- Jaune : 4 000 euros
- Violet : 3 000 euros
- Rose : Non éligible

Chaudière à granulés :
- Bleu : 10 000 euros
- Jaune : 8 000 euros
- Violet : 4 000 euros
- Rose : Non éligible

Chauffe-eau solaire :
- Bleu : 4 000 euros
- Jaune : 3 000 euros
- Violet : 2 000 euros
- Rose : Non éligible

Pompe à chaleur géothermique :
- Bleu : 11 000 euros
- Jaune : 9 000 euros
- Violet : 6 000 euros
- Rose : Non éligible

### Isolation

Isolation des murs par l'extérieur (par m²) :
- Bleu : 75 euros
- Jaune : 60 euros
- Violet : 40 euros
- Rose : 15 euros

Isolation des murs par l'intérieur (par m²) :
- Bleu : 25 euros
- Jaune : 20 euros
- Violet : 15 euros
- Rose : 7 euros

Isolation des combles perdus (par m²) :
- Bleu : 25 euros
- Jaune : 20 euros
- Violet : 15 euros
- Rose : 7 euros

Isolation des toitures terrasses (par m²) :
- Bleu : 75 euros
- Jaune : 60 euros
- Violet : 40 euros
- Rose : 15 euros

### Fenêtres et menuiseries

Remplacement de fenêtres simple vitrage (par équipement) :
- Bleu : 100 euros
- Jaune : 80 euros
- Violet : 40 euros
- Rose : Non éligible

### Ventilation

VMC double flux :
- Bleu : 2 500 euros
- Jaune : 2 000 euros
- Violet : 1 500 euros
- Rose : Non éligible

## MaPrimeRénov' Parcours Accompagné

Pour les rénovations d'ampleur, le parcours accompagné offre des aides bonifiées.

### Conditions

- Gain minimum de 2 classes énergétiques
- Accompagnement par Mon Accompagnateur Rénov'
- Sortie des passoires thermiques (F et G) obligatoire si concerné

### Taux de financement

Sortie de passoire thermique (F/G vers D ou mieux) :
- Bleu : jusqu'à 80% des travaux (plafond 70 000 euros)
- Jaune : jusqu'à 60% des travaux (plafond 55 000 euros)
- Violet : jusqu'à 45% des travaux (plafond 40 000 euros)
- Rose : jusqu'à 30% des travaux (plafond 30 000 euros)

## Les démarches pour obtenir MaPrimeRénov'

### Étape 1 : Créer un compte

Rendez-vous sur le site officiel maprimerenov.gouv.fr pour créer votre espace personnel.

### Étape 2 : Faire réaliser des devis

Faites établir des devis par des artisans certifiés RGE (Reconnu Garant de l'Environnement). Cette certification est obligatoire.

### Étape 3 : Déposer votre demande

Soumettez votre dossier en ligne avec les devis et justificatifs demandés. Ne commencez pas les travaux avant l'accusé de réception.

### Étape 4 : Réaliser les travaux

Une fois l'accord obtenu, vous avez généralement 2 ans pour réaliser les travaux.

### Étape 5 : Demander le versement

Après travaux, transmettez les factures pour recevoir le versement de la prime.

## Cumul avec d'autres aides

MaPrimeRénov' est cumulable avec :

- L'éco-prêt à taux zéro (éco-PTZ) : jusqu'à 50 000 euros à 0%
- Les certificats d'économie d'énergie (CEE) : primes des fournisseurs d'énergie
- Les aides locales : régions, départements, communes
- La TVA réduite à 5,5% : sur les travaux de rénovation énergétique

Le cumul total ne peut pas dépasser 90% du coût des travaux pour les ménages très modestes, et 80% pour les autres.

## Conseils pour maximiser vos aides

### 1. Faites réaliser un audit énergétique

L'audit permet d'identifier les travaux prioritaires et de planifier une rénovation cohérente.

### 2. Privilégiez la rénovation globale

Les aides sont plus importantes pour les rénovations d'ampleur que pour les gestes isolés.

### 3. Comparez les artisans RGE

Demandez plusieurs devis pour avoir le meilleur rapport qualité/prix.

### 4. Anticipez les délais

Les délais de traitement peuvent atteindre plusieurs semaines. Prévoyez vos travaux en conséquence.

### 5. Conservez tous les documents

Gardez précieusement les devis, factures et attestations pour justifier vos travaux.

## Questions fréquentes

### Puis-je commencer les travaux avant la réponse ?

Non, vous devez attendre l'accusé de réception de votre demande. Les travaux commencés avant cette date ne seront pas pris en charge.

### Combien de temps pour recevoir l'aide ?

Comptez environ 2 à 4 mois après la soumission des factures pour recevoir le virement.

### Les travaux réalisés soi-même sont-ils éligibles ?

Non, seuls les travaux réalisés par des professionnels RGE sont éligibles.

### Peut-on faire plusieurs demandes ?

Oui, vous pouvez cumuler plusieurs demandes pour différents travaux, dans la limite des plafonds par période de 5 ans.
    `
  },
];

export const BLOG_CATEGORIES = [
  "Tous",
  "Financement",
  "Investissement",
  "Achat",
  "Réglementation",
  "Fiscalité"
];
